<template>
  <v-menu v-bind="attrs.componentAttrs">
    <template #activator="{ props: menuProps }">
      <!-- We use a bare v-tooltip here because AppTooltip has trouble when combined with another activator -->
      <v-tooltip location="top" transition="fade-transition">
        <template #activator="{ props: tooltipProps }">
          <app-button
            class="add-item-button"
            type="icon"
            v-bind="{ ...menuProps, ...tooltipProps }"
            :data-locator="attrs.dataLocator"
          >
            <div class="icon-wrapper">
              <app-icon-ds
                icon="plus"
                size="xs"
                weight="far"
              />
            </div>
            <template v-if="!item">
              <span class="text-grey-800">{{ t('addChecklistItemButtonText') }}</span>
              <div class="icon-wrapper">
                <app-icon-ds
                  icon="regular-ds-chevron-down"
                  size="sm"
                  weight="fak"
                />
              </div>
            </template>
          </app-button>
        </template>
        <span>{{ t('addChecklistItemButtonTooltip') }}</span>
      </v-tooltip>
    </template>
    <v-list data-locator="other-actions-menu">
      <v-list-item
        link
        data-locator="add-task-menu-item"
        @click="createTask"
      >
        <v-list-item-title>
          <div class="d-flex align-center">
            <app-icon-ds
              icon="check-square"
              size="xs"
              weight="far"
              class="mr-3"
            />
            <span>{{ t('addTask') }}</span>
          </div>
        </v-list-item-title>
      </v-list-item>
      <!-- We can't set list item disabled cause the tooltip won't work -->
      <v-list-item
        link
        data-locator="add-section-menu-item"
        :class="{ 'text-greyDark cursor-default': shouldSectionAdditionBeDisabled }"
        @click="handleClickAddSection"
      >
        <AppTooltip v-if="shouldSectionAdditionBeDisabled">
          <template #activator="{ props }">
            <v-list-item-title v-bind="props">
              <app-icon-ds
                icon="regular-checklist-section"
                size="sm"
                weight="fak"
                class="mr-2"
              />
              <span>{{ t('addSection') }}</span>
            </v-list-item-title>
          </template>
          {{ t('addSectionDisableTooltip') }}
        </AppTooltip>
        <v-list-item-title v-else>
          <app-icon-ds
            icon="regular-checklist-section"
            size="sm"
            weight="fak"
            class="mr-2"
          />
          <span>{{ t('addSection') }}</span>
        </v-list-item-title>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import AppTooltip from '@/common/AppTooltip.vue'
import { getLastChild } from '@/common/utils/sections.js'
import { toast } from '@/common/utils/toast'
import { useTranslation } from '@/common/utils/translation'
import { useDataLocator } from '@/common/utils/useDataLocator'
import { CHECKLIST_OPTIONS } from '@/project/checklist/enums'
import { CREATE_TASK, CREATE_SECTION, EXPAND_SECTION } from '@/store/modules/checklist/action_types'

export default {
  name: 'ChecklistTableAddButton',
  inheritAttrs: false,
  components: {
    AppTooltip,
  },
  props: {
    item: {
      type: Object,
      required: false,
    },
    mnemo: {
      type: String,
      required: true,
    },
  },
  setup () {
    const {t} = useTranslation({ keyPrefix: 'project.checklist.ChecklistTableAddButton' })
    const { attrs } = useDataLocator()
    return { attrs, t }
  },
  computed: {
    ...mapGetters('checklist', ['flatChecklist']),
    shouldSectionAdditionBeDisabled () {
      // section depth is 0-indexed, hence the increment
      return this.item && this.item.depth >= CHECKLIST_OPTIONS.SECTION_MAX_DEPTH
    },
  },
  methods: {
    ...mapActions('checklist', [CREATE_TASK, CREATE_SECTION, EXPAND_SECTION]),

    /**
     * Base logic for creating a task or section.
     * @param {string} type - The type of checklist item to create ('task' or 'title').
     * @param {Object} defaultData - The default data structure for the checklist item.
     * @param {string} successMessage - The success message to display on creation.
     * @param {string} errorMessage - The error message to display on failure.
     */
    async createChecklistItem (type, defaultData, successMessage, errorMessage) {
      try {
        let data = defaultData

        if (this.item) {
          data = this.prepareAddChecklistItemButtonAction(type)
          data.title = ''
        } else if (this.flatChecklist.length > 0) {
          const lastElement = this.flatChecklist[this.flatChecklist.length - 1]
          data.prevId = lastElement.id
          data.prevType = lastElement.type
        }

        const action = type === 'task' ? this.CREATE_TASK : this.CREATE_SECTION
        await action({ mnemo: this.mnemo, data })
        toast.success(this.t(successMessage))
      } catch (e) {
        console.error(e)
        toast.error(this.t(errorMessage))
      }
    },

    createTask () {
      this.createChecklistItem(
        'task',
        { prevId: null, prevType: null },
        'createTaskSuccess',
        'createTaskError',
      )
    },

    createSection () {
      this.createChecklistItem(
        'title',
        { title: '', depth: 0, prevId: null, prevType: null },
        'createSectionSuccess',
        'createSectionError',
      )
    },

    /**
     * Get the base payload for adding a checklist item.
     * @param {object} [cachedLastChild] - The last child of the item if already computed.
     * @param {string} addItemType - ('title' or 'task').
     * @returns {Object} The payload for the add checklist item button action.
     */
    getAddChecklistItemBasePayload (cachedLastChild, addItemType ) {
      const payload = {
        prevId: this.item.id,
        prevType: this.item.type,
        depth: this.item.depth,
      }
        if (!cachedLastChild) {
          cachedLastChild = getLastChild(this.item).item
        }
        payload.prevId = cachedLastChild.id
        payload.prevType = cachedLastChild.type
        payload.depth = addItemType ==='title'? this.item.depth + 1 :this.item.depth
      return payload
    },

    /**
     * Prepare and retrieve the base payload for any add checklist item action.
     * @param {string} addItemType - The item type to being prepared ('title' or 'task').
     * @returns {Object} The base payload for the action.
     */
    prepareAddChecklistItemButtonAction (addItemType) {
      let lastChild
      if (this.item.type === 'title') {
        const lastSectionChild = getLastChild(this.item, (lastSectionChildResult) => {
          if (addItemType === 'title' && lastSectionChildResult.parent.type === 'task') {
            return
          }
          this.EXPAND_SECTION(lastSectionChildResult.parent.id)
        })
        lastChild = lastSectionChild?.item ?? this.item
      } else {
        lastChild = this.item
      }
      return this.getAddChecklistItemBasePayload(lastChild, addItemType)
    },
    handleClickAddSection (event) {
      if (this.shouldSectionAdditionBeDisabled) {
        // avoid closing the menu when clicking
        event.stopPropagation()
      } else {
        this.createSection()
      }
    },
  },
}
</script>
<style scoped lang="scss">
.v-list-item-title {
  display: flex;
  align-items: center;
}

.add-item-button.app-button.app-button--icon {
  min-width: 28px;
  min-height: 28px;
  padding-inline: 12px;
  height: 28px;

  .icon-wrapper {
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  :deep(span) {
    column-gap: 4px;
  }
}
</style>
